'use client'

import { useEffect, useRef } from 'react'

// needs to be updated when the types of inputs in our webshops change
const EDITABLE_INPUT_TYPES = new Set([
  'checkbox',
  'number',
  'password',
  'search',
  'tel',
  'text',
  'time',
  'url',
])

const isEditableFocused = (): boolean => {
  const element = document.activeElement as HTMLElement | null
  if (!element) {
    return false
  }

  const tag = element.tagName

  if (tag === 'TEXTAREA' || tag === 'SELECT') {
    return true
  }

  if (tag === 'INPUT') {
    const inputElement = element as HTMLInputElement

    return EDITABLE_INPUT_TYPES.has(inputElement.type)
  }

  return false
}

/**
 * Run a callback after the user stops focusing on an editable field
 */
export const useIdleModalTimer = (delay: number, onIdle: () => void): void => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clear = (): void => {
    if (timerRef.current !== null) {
      globalThis.clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  useEffect(() => {
    const startTimer = (): void => {
      clear()
      timerRef.current = globalThis.setTimeout(() => {
        onIdle()
        timerRef.current = null
      }, delay)
    }

    const handleFocusIn = (): void => {
      clear()
    }
    const handleFocusOut = (): void => {
      if (!isEditableFocused()) {
        startTimer()
      }
    }

    if (!isEditableFocused()) {
      startTimer()
    }

    document.addEventListener('focusin', handleFocusIn)
    document.addEventListener('focusout', handleFocusOut)

    return (): void => {
      clear()
      document.removeEventListener('focusin', handleFocusIn)
      document.removeEventListener('focusout', handleFocusOut)
    }
  }, [delay, onIdle])
}
